import { Card } from '@/components/common/Card';
import StaticPageLayout from '@/components/layout/StaticPageLayout';
import { motion } from 'framer-motion';
import { BookOpen } from 'lucide-react';
import type { ReactNode } from 'react';

interface GuideStep {
  title: string;
  description: string;
  tips: string[];
  icon?: React.ReactNode;
}

interface GuidePageLayoutProps {
  title: string;
  description: string;
  steps: GuideStep[];
  children?: ReactNode;
}

export function GuidePageLayout({
  title,
  description,
  steps,
  children,
}: GuidePageLayoutProps) {
  // Define the rotating gradient patterns to match features page
  const gradients = [
    'from-primary-500 to-accent-blue',
    'from-accent-blue to-accent-purple',
    'from-accent-purple to-accent-pink',
  ];

  return (
    <StaticPageLayout>
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="relative flex min-h-[calc(100vh-64px)] items-center justify-center overflow-hidden">
          <div className="layout relative z-10 mx-auto flex flex-col items-center justify-center px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mx-auto mb-4 text-center"
            >
              <span className="inline-flex items-center rounded-full bg-primary-50 px-2 py-1 text-sm font-medium text-primary-700 shadow-sm">
                <BookOpen className="mr-1 h-4 w-4" /> Step-by-Step Guide
              </span>
            </motion.div>

            <div className="text-center">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl md:text-5xl lg:text-6xl"
              >
                {title.includes(' ') ? (
                  title.split(' ').map((word, i, arr) =>
                    i === arr.length - 1 ? (
                      <span
                        key={i}
                        className="bg-gradient-to-r from-primary-600 to-accent-blue bg-clip-text text-transparent"
                      >
                        {word}
                      </span>
                    ) : (
                      word + ' '
                    )
                  )
                ) : (
                  <span className="bg-gradient-to-r from-primary-600 to-accent-blue bg-clip-text text-transparent">
                    {title}
                  </span>
                )}
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="mx-auto mt-4 max-w-3xl text-base text-gray-600 sm:mt-6 sm:text-lg md:text-xl"
              >
                {description}
              </motion.p>
            </div>
          </div>
        </section>

        {/* Steps Section */}
        <section className="relative py-8 md:py-16">
          <div className="layout">
            <div className="space-y-8">
              <div className="relative">
                {/* Step Timeline */}
                <div className="absolute left-8 top-0 hidden h-full w-px bg-gradient-to-b from-primary-500 via-primary-500/20 to-transparent sm:block" />

                {steps.map((step, index) => (
                  <motion.div
                    key={step.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="relative pb-8"
                  >
                    <div className="flex flex-col items-center space-y-4 sm:flex-row sm:items-start sm:space-x-6 sm:space-y-0">
                      {/* Step Icon/Number */}
                      <div
                        className={`relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-r ${gradients[index % gradients.length]} text-white shadow-md`}
                      >
                        {step.icon || (
                          <div className="text-xl font-bold">{index + 1}</div>
                        )}
                        <div className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-sm font-semibold text-primary-700">
                          {index + 1}
                        </div>
                      </div>

                      {/* Step Content */}
                      <div className="w-full flex-1">
                        <Card allowOverflow={false} className="md:p-0">
                          <div
                            className={`rounded-t-lg bg-gradient-to-r ${gradients[index % gradients.length]} p-4`}
                          >
                            <h3 className="text-lg font-semibold text-white">
                              {step.title}
                            </h3>
                            <p className="text-sm text-white">
                              {step.description}
                            </p>
                          </div>

                          {step.tips && step.tips.length > 0 && (
                            <div className="p-4">
                              <ul className="space-y-2">
                                {step.tips.map((tip, tipIndex) => (
                                  <li
                                    key={tipIndex}
                                    className="flex items-center text-sm text-gray-600"
                                  >
                                    <span className="mr-2 h-1.5 w-1.5 rounded-full bg-primary-500"></span>
                                    {tip}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </Card>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Additional Content */}
        {children && (
          <section className="relative py-8 md:py-16">
            <div className="layout">{children}</div>
          </section>
        )}
      </main>
    </StaticPageLayout>
  );
}
